import type { DevicePortRole, PoEInSpec, PoEOutSpec, PortRoleKind } from '../../types/deviceModel';
import type { FacedPort } from './layoutPorts';

export function facedPortKey(port: FacedPort): string {
  return `${port.face}:${port.number}:${port.kind}`;
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

function specsEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function nextRoleId(kind: PortRoleKind, roles: DevicePortRole[]): string {
  let n = 1;
  while (roles.some((r) => r.id === `role-${kind}-${n}`)) n += 1;
  return `role-${kind}-${n}`;
}

function nextRoleName(kind: PortRoleKind, roles: DevicePortRole[]): string {
  const count = roles.filter((r) => r.kind === kind).length;
  const base = kind === 'poe-in' ? 'PoE-in' : 'PoE-out';
  return count === 0 ? base : `${base} ${count + 1}`;
}

export function createPortRole(kind: PortRoleKind, spec: PoEInSpec | PoEOutSpec, roles: DevicePortRole[]): DevicePortRole {
  const id = nextRoleId(kind, roles);
  const name = nextRoleName(kind, roles);
  if (kind === 'poe-in') return { id, kind, name, poeIn: clone(spec as PoEInSpec) };
  return { id, kind, name, poeOut: clone(spec as PoEOutSpec) };
}

export function applyRoleToPort(port: FacedPort, role: DevicePortRole): FacedPort {
  if (role.kind === 'poe-in' && role.poeIn) {
    return { ...port, poeIn: clone(role.poeIn), poeInRoleId: role.id };
  }
  if (role.kind === 'poe-out' && role.poeOut) {
    return { ...port, poeOut: clone(role.poeOut), poeOutRoleId: role.id };
  }
  return port;
}

export function unapplyRoleFromPort(port: FacedPort, role: DevicePortRole): FacedPort {
  if (role.kind === 'poe-in' && port.poeInRoleId === role.id) {
    const { poeIn: _in, poeInRoleId: _id, ...rest } = port;
    return rest as FacedPort;
  }
  if (role.kind === 'poe-out' && port.poeOutRoleId === role.id) {
    const { poeOut: _out, poeOutRoleId: _id, ...rest } = port;
    return rest as FacedPort;
  }
  return port;
}

export function portHasRole(port: FacedPort, role: DevicePortRole): boolean {
  if (role.kind === 'poe-in') return port.poeInRoleId === role.id;
  return port.poeOutRoleId === role.id;
}

export function syncRoleSpecToPorts(ports: FacedPort[], role: DevicePortRole): FacedPort[] {
  return ports.map((port) => {
    if (!portHasRole(port, role)) return port;
    return applyRoleToPort(port, role);
  });
}

/** Group existing port specs into shared roles and stamp role ids onto ports. */
export function synthesizePortRoles(
  ports: FacedPort[],
  existing?: DevicePortRole[]
): { roles: DevicePortRole[]; ports: FacedPort[] } {
  const roles: DevicePortRole[] = (existing || []).map((role) => ({
    ...role,
    poeIn: role.poeIn ? clone(role.poeIn) : undefined,
    poeOut: role.poeOut ? clone(role.poeOut) : undefined,
  }));

  const findOrCreate = (kind: PortRoleKind, spec: PoEInSpec | PoEOutSpec, preferredId?: string): DevicePortRole => {
    const byId = preferredId ? roles.find((r) => r.id === preferredId && r.kind === kind) : undefined;
    if (byId) return byId;
    const bySpec = roles.find((r) => {
      if (r.kind !== kind) return false;
      return kind === 'poe-in' ? specsEqual(r.poeIn, spec) : specsEqual(r.poeOut, spec);
    });
    if (bySpec) return bySpec;
    const created = createPortRole(kind, spec, roles);
    roles.push(created);
    return created;
  };

  const nextPorts = ports.map((port) => {
    let next = { ...port };
    if (next.poeIn) {
      const role = findOrCreate('poe-in', next.poeIn, next.poeInRoleId);
      next = { ...next, poeIn: clone(role.poeIn!), poeInRoleId: role.id };
    } else {
      const { poeInRoleId: _id, ...rest } = next;
      next = rest as FacedPort;
    }
    if (next.poeOut) {
      const role = findOrCreate('poe-out', next.poeOut, next.poeOutRoleId);
      next = { ...next, poeOut: clone(role.poeOut!), poeOutRoleId: role.id };
    } else {
      const { poeOutRoleId: _id, ...rest } = next;
      next = rest as FacedPort;
    }
    return next;
  });

  return { roles, ports: nextPorts };
}
